import React, { useState, useEffect, useRef } from "react";
import {
  Handle,
  Position,
  NodeProps,
  useReactFlow,
  useEdges,
  useNodes,
} from "reactflow";
import { OctagonX, FlaskConical } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { Button } from "@components/ui/button";
import Confirmation from "@components/common/ConfirmDialog";
import { toast } from "@components/ui/use-toast";
import { getConnectedInputs, getExecutionPath } from "../common/nodeDataFlow";
import { executeWorkflow } from "../createPluginApis";
import { ValidatedTitleInput } from "../components/ValidatedTitleInput";
import PaginatedInputViewer from "../common/PaginatedInputViewer";
import ResponseViewer from "../common/ResponseViewer";
import NodeContextMenu, {
  useNodeContextMenu,
  createContextMenuItems,
} from "../common/NodeContextMenu";
import CollapsedNodeView from "../common/NodeHeaderView";
import NodeContentSection from "../common/NodeContentSection";
import { FullscreenWrapper } from "../common/withFullscreen";
import { FullscreenToggleButton } from "../common/FullscreenToggleButton";
import { useResizablePanels } from "../hooks/useResizablePanels";
import { PanelDivider } from "../common/PanelDivider";
import {
  getNodeExecutionParameters,
  buildWorkflowPayload,
  handleNodeExecutionResults,
  findDownstreamNodes,
} from "../utils/nodeExecutionUtils";
import { getNodeBackendType } from "../utils/nodeTransformUtils";
import { handlerStyle, unwrapJsonData } from "../common/commonData";

interface StopAndErrorNodeData {
  label?: string;
  header?: string;
  uid: string;
  title: string;
  description?: string;
  errorType?: "errorMessage" | "errorObject";
  errorMessage?: string;
  onDelete?: (id: string) => void;
  onDataChange?: (id: string, updates: any) => void;
  borderColor?: string;
  shadowColor?: string;
}

const StopAndErrorNode: React.FC<NodeProps<StopAndErrorNodeData>> = ({
  id,
  data,
}) => {
  const label = data.label || data.header || "Stop and Error";
  const [uid, setUid] = useState(data.uid || "");
  const [title, setTitle] = useState(data.title || "");
  const [description, setDescription] = useState(data.description || "");
  const [isTitleValid, setIsTitleValid] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Context menu
  const {
    showContextMenu,
    contextMenuPosition,
    handleContextMenu,
    closeContextMenu,
  } = useNodeContextMenu(id);

  // Parameter state
  const [errorType, setErrorType] = useState<"errorMessage" | "errorObject">(
    data.errorType || "errorMessage",
  );
  const [errorMessage, setErrorMessage] = useState(
    data.errorMessage || "HTTP request failed with status code 500",
  );

  // Input/Output state
  const [inputData, setInputData] = useState<any[]>([]);
  const [outputData, setOutputData] = useState<any>(null);
  const [errorResponse, setErrorResponse] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const reactFlowInstance = useReactFlow();
  const { containerRef, widths, startDrag } = useResizablePanels([
    33.33, 33.33, 33.34,
  ]);

  // Ref to track previous outputData and prevent unnecessary updates
  const prevOutputDataRef = useRef<any>(null);

  // Get edges and nodes using hooks for proper reactivity
  const edges = useEdges();
  const nodes = useNodes();

  // Copy output to clipboard
  const handleCopyOutput = () => {
    const dataToCopy = errorResponse || outputData;
    if (!dataToCopy) return;
    navigator.clipboard.writeText(JSON.stringify(dataToCopy, null, 2));
    toast({
      variant: "success",
      title: "Copied!",
      description: "Output copied to clipboard.",
      duration: 2000,
    });
  };

  // Manual refresh function for InputViewer
  const refreshInputData = React.useCallback(() => {
    const inputs = getConnectedInputs(id, edges, nodes);
    setInputData(inputs);
  }, [id, edges, nodes]);

  // Get input data from connected nodes - use stringified comparison to avoid infinite loops
  useEffect(() => {
    const inputs = getConnectedInputs(id, edges, nodes);
    setInputData(inputs);
  }, [
    id,
    JSON.stringify(edges.filter((e) => e.target === id || e.source === id)),
    JSON.stringify(
      nodes.map((n) => ({
        id: n.id,
        configData: (n.data as any)?.configData,
        response: (n.data as any)?.response,
        outputData: (n.data as any)?.outputData,
      })),
    ),
  ]);

  // Sync outputData from node data (updated during workflow/node execution)
  useEffect(() => {
    const newOutputData = (data as any).outputData;

    if (!newOutputData) {
      if (outputData !== null && newOutputData === null) {
        setOutputData(null);
      }
      return;
    }

    if (prevOutputDataRef.current === newOutputData) {
      return;
    }

    const newDataStr = JSON.stringify(newOutputData);
    const prevDataStr = prevOutputDataRef.current
      ? JSON.stringify(prevOutputDataRef.current)
      : null;

    if (newDataStr !== prevDataStr) {
      prevOutputDataRef.current = newOutputData;
      setOutputData(newOutputData);
    } else {
      prevOutputDataRef.current = newOutputData;
    }
  }, [(data as any).outputData]);

  // Sync error from node data (set during global workflow execution)
  useEffect(() => {
    const executionStatus = (data as any).executionStatus;
    const error = (data as any).error;

    // If node failed during global execution, show error in ResponseViewer
    if (executionStatus === "failed" && error) {
      setErrorResponse(error);
    } else if (executionStatus === "success") {
      // Clear error when node succeeds
      setErrorResponse(null);
    }
  }, [(data as any).executionStatus, (data as any).error]);

  // Sync data changes with parent
  useEffect(() => {
    if (!data.onDataChange) return;

    const storedParameters: any = {
      errorType,
      errorMessage,
    };

    data.onDataChange(id, {
      uid,
      title,
      description,
      errorType,
      errorMessage,
      outputData,
      response: outputData,
      type: "error",
      sub_type: "stop_and_error",
      storedParameters,
    });
  }, [uid, title, description, errorType, errorMessage, outputData, id]);

  // Generate UID on mount
  useEffect(() => {
    if (!uid) {
      const newUid = `stop_and_error_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      setUid(newUid);
    }
  }, [uid]);

  // Load saved parameters from data prop (when loading from backend)
  useEffect(() => {
    const params = (data as any).storedParameters || {};

    if (params.errorType) setErrorType(params.errorType);
    if (params.errorMessage !== undefined) setErrorMessage(params.errorMessage);
  }, []); // Only run once on mount

  // Execute stop-and-error operation
  const executeStopAndErrorNode = async () => {
    if (!errorMessage.trim()) {
      toast({
        variant: "destructive",
        title: "No Error Message",
        description: "Enter an error message",
      });
      return;
    }

    if (!title.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Title is required for the stop and error node",
        duration: 3000,
      });
      return;
    }

    const pluginId = sessionStorage.getItem("editPluginId");

    if (!pluginId || pluginId === "temp-plugin") {
      toast({
        variant: "destructive",
        title: "Plugin Not Saved",
        description: "Please save the plugin first before testing",
        duration: 4000,
      });
      return;
    }

    const activeMeshFlowStr = sessionStorage.getItem("activeMeshFlow");
    if (!activeMeshFlowStr) {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          "No active mesh flow. Please create or select a mesh flow first.",
        duration: 4000,
      });
      return;
    }

    setOutputData(null);
    setErrorResponse(null);
    setLoading(true);

    try {
      const activeMeshFlow = JSON.parse(activeMeshFlowStr);
      const allNodes = reactFlowInstance.getNodes();
      const allEdges = reactFlowInstance.getEdges();
      const executableNodes = getExecutionPath(id, allNodes, allEdges);

      // Mark all executable nodes as pending
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          const isExecutable = executableNodes.some(
            (n: any) => n.id === node.id,
          );
          if (isExecutable) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "pending",
                executionResult: undefined,
                error: undefined,
              },
            };
          }
          return node;
        }),
      );

      // Execute workflow ONCE with all nodes up to and including this node
      const workflowNodes = executableNodes.map((node: any) => {
        let nodeType = "core";
        let subType = "http_request";
        let parameters;

        if (node.id === id) {
          // This is the current StopAndErrorNode - build from local state
          const stopAndErrorParameters: any = {
            errorType,
            errorMessage,
          };

          nodeType = "error";
          subType = "stop_and_error";
          parameters = {
            ...stopAndErrorParameters,
            label: label,
          };
        } else {
          // Other nodes - use centralized utility (checks storedParameters first)
          const backendTypeInfo = getNodeBackendType(node.type || "custom");
          nodeType = backendTypeInfo.type;
          subType = backendTypeInfo.sub_type;

          parameters = getNodeExecutionParameters(node, pluginId).parameters;
        }

        return {
          uid: node.data.uid || node.id,
          title: node.data.title || node.data.label || "Untitled",
          description: node.data.description || "",
          type: nodeType,
          sub_type: subType,
          version: "1.0",
          position: [node.position.x, node.position.y],
          parameters,
        };
      });

      // Build connections between nodes
      const nodeIdToUidMap: Record<string, string> = {};
      executableNodes.forEach((node: any) => {
        nodeIdToUidMap[node.id] = node.data.uid || node.id;
      });

      const executionPathNodeIds = new Set(
        executableNodes.map((n: any) => n.id),
      );

      const connections: any = {};
      allEdges
        .filter(
          (edge) =>
            executionPathNodeIds.has(edge.source) &&
            executionPathNodeIds.has(edge.target),
        )
        .forEach((edge) => {
          const sourceUid = nodeIdToUidMap[edge.source];
          const targetUid = nodeIdToUidMap[edge.target];

          if (!sourceUid || !targetUid) return;

          if (!connections[sourceUid]) {
            connections[sourceUid] = {
              main: [],
            };
          }

          const sourceNode = executableNodes.find(
            (n: any) => n.id === edge.source,
          );
          const isIfElseNode =
            sourceNode?.type === "ifElseNode" ||
            sourceNode?.data?.sub_type === "if_else";

          let outputIndex = 0;
          if (isIfElseNode && edge.sourceHandle) {
            outputIndex = edge.sourceHandle === "false" ? 1 : 0;
          }

          if (!connections[sourceUid].main[outputIndex]) {
            connections[sourceUid].main[outputIndex] = [];
          }

          connections[sourceUid].main[outputIndex].push({
            node: targetUid,
            type: "main",
            index: 0,
          });
        });

      const workflowPayload = buildWorkflowPayload(
        activeMeshFlow,
        workflowNodes,
        connections,
      );

      const result = await executeWorkflow(workflowPayload);

      // Update ALL executed nodes with their results (not just the clicked node)
      if (result?.results?.[0]?.node_results) {
        const nodeResults = result.results[0].node_results;

        const {
          hasErrors,
          errorMessage: execErrorMessage,
          errorNodeTitle,
        } = handleNodeExecutionResults(
          reactFlowInstance,
          nodeResults,
          allEdges,
        );

        const resultsMap = new Map<string, any>();
        nodeResults.forEach((nodeResult: any) => {
          resultsMap.set(nodeResult.uid, nodeResult);
        });

        const currentNodeResult = resultsMap.get(uid || id);
        if (currentNodeResult) {
          const isFailed =
            currentNodeResult.status === "error" ||
            currentNodeResult.status === "failed" ||
            currentNodeResult.status === "failure";

          if (isFailed) {
            // Show error in ResponseViewer but don't pass to downstream nodes
            setErrorResponse(currentNodeResult.error || "Execution failed");
            setOutputData(null);
          } else if (currentNodeResult.output_data) {
            const responseData = unwrapJsonData(currentNodeResult.output_data);
            setOutputData(responseData);
            setErrorResponse(null);
          }
        }

        // Show appropriate toast based on execution result
        if (hasErrors) {
          const errorDescription = execErrorMessage
            ? `${errorNodeTitle}: ${execErrorMessage}`
            : "One or more nodes failed during execution";

          const shortError =
            errorDescription.length > 150
              ? errorDescription.substring(0, 150) + "..."
              : errorDescription;

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description: shortError,
            duration: 7000,
          });
        } else {
          toast({
            variant: "success",
            title: "Success",
            description: "Stop and Error executed successfully",
            duration: 3000,
          });
        }
      } else {
        // Fallback - no node results in response
        const isSuccess =
          result?.success === true ||
          result?.status === "success" ||
          result?.body?.status === 1 ||
          result?.body?.status === "1" ||
          result?.status === 1;

        if (isSuccess) {
          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "success",
                  },
                };
              }
              return node;
            }),
          );

          toast({
            variant: "success",
            title: "Success",
            description:
              result?.message || "Stop and Error executed successfully",
            duration: 3000,
          });
        } else {
          const allEdges = reactFlowInstance.getEdges();
          const downstreamNodes = findDownstreamNodes(id, allEdges);

          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error:
                      result?.body?.message ||
                      result?.message ||
                      "Execution failed",
                  },
                };
              } else if (downstreamNodes.has(node.id)) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error: "Not executed due to upstream failure",
                  },
                };
              }
              return node;
            }),
          );

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description:
              result?.body?.message ||
              result?.message ||
              "Stop and Error execution failed",
            duration: 4000,
          });
        }
      }
    } catch (error: any) {
      console.error("Stop and Error Execution Error:", error);
      setErrorResponse(error?.message || "Stop and Error execution failed");
      setOutputData(null);

      const allEdges = reactFlowInstance.getEdges();
      const downstreamNodes = findDownstreamNodes(id, allEdges);

      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: error?.message || "Stop and Error execution failed",
              },
            };
          } else if (downstreamNodes.has(node.id)) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: "Not executed due to upstream failure",
              },
            };
          }
          return node;
        }),
      );

      toast({
        variant: "destructive",
        title: "Error",
        description: error?.message || "Stop and Error execution failed",
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (nodeId: string) => {
    setDeleteId(nodeId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (deleteId && data.onDelete) {
      data.onDelete(deleteId);
    }
    setShowDeleteConfirm(false);
    setDeleteId(null);
  };

  // Extract and merge input data from connected nodes
  const getDisplayData = (input: any) => {
    const EXCLUDE = [
      "uid",
      "isDecript",
      "isDecrypted",
      "title",
      "testConnectionBtn",
      "tabId",
      "configType",
      "onDelete",
      "onDataChange",
      "header",
      "borderColor",
      "shadowColor",
      "label",
      "nodeType",
      "itemCount",
    ];

    const extractJsonData = (data: any) => {
      if (Array.isArray(data)) {
        return data.map((item) => {
          if (item && typeof item === "object" && "json_data" in item) {
            return item.json_data;
          }
          return item;
        });
      }
      if (data && typeof data === "object" && "json_data" in data) {
        return data.json_data;
      }
      return data;
    };

    if (input.outputData) {
      const extracted = extractJsonData(input.outputData);
      return extracted;
    }

    if (input.response) {
      const extracted = extractJsonData(input.response);
      return extracted;
    }

    const displayData: any = {};
    if (input.configData) {
      const encryptedFields = input.configData.isDecript || [];

      Object.entries(input.configData)
        .filter(([key]) => !EXCLUDE.includes(key))
        .forEach(([key, value]) => {
          if (Array.isArray(encryptedFields) && encryptedFields.includes(key)) {
            displayData[key] = "********************";
          } else {
            displayData[key] = value;
          }
        });
    }
    return displayData;
  };

  // Render input data section
  const renderInputData = () => {
    if (!inputData?.length) {
      return (
        <div className="text-xs text-muted-foreground p-4 border rounded-md bg-muted/20 text-center">
          <div className="font-medium">No Input Connected</div>
          <div>Connect a node to see data here</div>
        </div>
      );
    }

    const sources = inputData.map((input, index) => {
      let displayLabel = input.label || `Source ${index + 1}`;

      if (displayLabel.toLowerCase().includes("switch")) {
        displayLabel = `Rule ${index + 1}`;
      }

      return {
        label: displayLabel,
        data: getDisplayData(input),
        isConfig: !!input.configData,
      };
    });

    return (
      <PaginatedInputViewer
        sources={sources}
        onRefresh={refreshInputData}
        sourceCount={inputData.length}
      />
    );
  };

  return (
    <FullscreenWrapper
      id={id}
      isExpanded={isExpanded}
      executionStatus={(data as any).executionStatus}
      borderColor={data.borderColor}
      shadowColor={data.shadowColor}
      onContextMenu={handleContextMenu}
      expandedClassName="w-[90vw] !max-w-[1000px] flex flex-col"
    >
      <Confirmation
        text="Are you sure you want to delete this node?"
        confirmAction={confirmDelete}
        rejectAction={() => setShowDeleteConfirm(false)}
        confirmText="Confirm"
        rejectText="Cancel"
        show={showDeleteConfirm}
        closeDialog={() => setShowDeleteConfirm(false)}
      />

      {/* Context Menu */}
      <NodeContextMenu
        show={showContextMenu}
        position={contextMenuPosition}
        onClose={closeContextMenu}
        items={[
          createContextMenuItems.expandCollapse(isExpanded, () =>
            setIsExpanded((prev) => !prev),
          ),
          createContextMenuItems.delete(
            () => handleDelete(id),
            !!data.onDelete,
          ),
        ]}
      />

      {/* Handles */}
      <Handle type="target" position={Position.Left} className={handlerStyle} />
      <Handle
        type="source"
        position={Position.Right}
        className={handlerStyle}
      />

      {/* Header */}
      <div
        className={`flex items-center justify-between gap-4 rounded-md transition-all duration-500 flex-shrink-0 ${
          isExpanded ? "p-3" : "!p-3"
        }`}
      >
        <CollapsedNodeView
          icon={
            <OctagonX
              className={
                (data as any).executionStatus === "running" ||
                (data as any).executionStatus === "pending"
                  ? "animate-bounce"
                  : ""
              }
            />
          }
          label={label}
          title={title}
          iconSize={35}
          borderColor={data.borderColor}
          onToggle={() => setIsExpanded((prev) => !prev)}
          isExpanded={isExpanded}
          executionStatus={(data as any).executionStatus}
          description={description}
        />
        <FullscreenToggleButton isExpanded={isExpanded} />
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* Metadata Form */}
          <div className="mt-2 p-4 border-t flex items-start gap-4 flex-shrink-0">
            <div className="space-y-1 w-1/2">
              <label className="text-xs font-medium text-muted-foreground">
                Node Title <span className="text-primary">*</span>
              </label>
              <ValidatedTitleInput
                id={id}
                title={title}
                onChange={setTitle}
                nodes={nodes}
                dataTitle={data.title}
                onValidationChange={setIsTitleValid}
              />
            </div>
            <div className="space-y-1 w-1/2">
              <label className="text-xs font-medium text-muted-foreground">
                Node Description (optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter node description"
                className="w-full p-2 border rounded text-xs resize-none text-muted-foreground bg-background"
                rows={1}
              />
            </div>
          </div>

          {/* 3 Section Grid */}
          <div
            ref={containerRef}
            className="flex gap-0 p-4 border-t flex-1 min-h-0 overflow-hidden"
          >
            {/* Left Section - Input Data (4/12 columns) */}
            <div
              style={{ width: `${widths[0]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection scrollable={false} hasRelative={true}>
                {renderInputData()}
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(0)} />

            {/* Middle Section - Config (4/12 columns) */}
            <div
              style={{ width: `${widths[1]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection hasRelative={true} scrollable={false}>
                <div className="flex-1 min-h-0 flex flex-col">
                  {/* Single scroll surface — wraps all scrollable content */}
                  <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col gap-2 relative">
                    <h3 className="text-sm font-semibold sticky top-0 bg-background z-10 pb-2 border-b">
                      Parameters
                    </h3>

                    {/* Error Type */}
                    <div className="space-y-2 border rounded-md p-3 bg-muted/20 mt-4">
                      <label className="text-xs font-semibold">
                        Error Type
                      </label>
                      <Select
                        value={errorType}
                        onValueChange={(
                          value: "errorMessage" | "errorObject",
                        ) => setErrorType(value)}
                      >
                        <SelectTrigger className="w-full text-xs h-8">
                          <SelectValue placeholder="Select error type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectItem
                              value="errorMessage"
                              className="text-xs"
                            >
                              Error Message
                            </SelectItem>
                            <SelectItem value="errorObject" className="text-xs">
                              Error Object
                            </SelectItem>
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>

                    <h3 className="text-sm font-semibold sticky top-0 bg-background z-10 pb-2 border-b">
                      Error Configuration
                    </h3>

                    {/* Error Message */}
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-muted-foreground">
                        Error Message <span className="text-primary">*</span>
                      </label>
                      <textarea
                        value={errorMessage}
                        onChange={(e) => setErrorMessage(e.target.value)}
                        placeholder="e.g., HTTP request failed with status code 500"
                        className="w-full p-2 border rounded text-xs resize-none text-foreground bg-background h-24"
                      />
                      <div className="text-xs text-muted-foreground">
                        This message will be raised as the workflow error when
                        this node executes
                      </div>
                    </div>
                  </div>
                  {/* Execute Button */}
                  <div className="sticky -bottom-1 left-0 right-0 bg-background border-t">
                    <Button
                      onClick={executeStopAndErrorNode}
                      disabled={
                        !isTitleValid || !title.trim() || !errorMessage.trim()
                      }
                      className="w-full mt-4 sticky bottom-0 left-0 right-0 rounded-b-md"
                      size="sm"
                    >
                      <FlaskConical size={14} className="mr-2" />
                      Execute step
                    </Button>
                  </div>
                </div>
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(1)} />

            {/* Right Section - Response (4/12 columns) */}
            <div style={{ flex: 1, minWidth: 80 }}>
              <NodeContentSection scrollable={false} hasRelative={true}>
                <ResponseViewer
                  response={errorResponse || outputData}
                  loading={loading}
                  emptyMessage="Execute the node to see output preview"
                  loadingMessage="Processing..."
                  onCopy={handleCopyOutput}
                  isError={!!errorResponse}
                  isLoopChild={(data as any).is_loop_child || false}
                  totalIterations={(data as any).total_iterations || 0}
                  iterations={(data as any).iterations || []}
                  fillParent={true}
                  onResponseChange={(newData) => {
                    setOutputData(newData);
                    data.onDataChange?.(id, {
                      response: newData,
                      manuallyEdited: true,
                    });
                  }}
                />
              </NodeContentSection>
            </div>
          </div>
        </div>
      )}
    </FullscreenWrapper>
  );
};

export default StopAndErrorNode;
